// src/features/patient/care-treatment/utils/careNaming.ts

/**
 * Purpose : The plain names the Care & Treatment screens use: a
 * record's type ("Phone call", never "Telephone"), its icon and tile colour,
 * and the words a patient must never see on these screens. The backend's
 * typeLabel follows the same map; this is the fallback for an older server.
 *
 * Contents :
 *
 * * const(RECORD_TYPE_NAMES) : printed record type → plain name
 * * function(recordTypeLabel) : an encounter's plain type name
 * * const(KIND_META) / function(visitKind) / function(kindMeta) : icon and tile per kind
 * * const(BANNED_STRINGS) : wording that never renders on a Care & Treatment screen
 */

import type { Encounter, EncounterClass, VisitKind } from "@/features/patient/care-treatment/api/careTimelineTypes";
import { classMeta } from "@/features/patient/care-treatment/utils/careTimeline";

// const(RECORD_TYPE_NAMES)
/** Keys are the printed type, lower-cased. */
export const RECORD_TYPE_NAMES: Record<string, string> = {
  telephone: "Phone call",
  "patient message": "Portal message",
  "letter (out)": "Letter",
  "results encounter": "Results reviewed",
  documentation: "Chart note",
  abstract: "Chart summary",
  "scanned document": "Scanned document",
};

const NO_LABEL = "Other record";

// function(recordTypeLabel)
/**
 * The record's plain type: the backend's typeLabel, else the printed type
 * mapped, else the class's name. "Other record" only when there is no label.
 *
 * @param encounter - The visit or record.
 * @returns e.g. "Phone call", "Prenatal visit".
 */
export function recordTypeLabel(encounter: Pick<Encounter, "typeLabel" | "typeRaw" | "class">): string {
  const label = encounter.typeLabel?.trim();
  if (label) return label;
  const mapped = RECORD_TYPE_NAMES[encounter.typeRaw?.trim().toLowerCase() ?? ""];
  if (mapped) return mapped;
  return classMeta(encounter.class).label || NO_LABEL;
}

// const(KIND_META)
/**
 * Icon and tile colours per kind. Every colour pair is one the platform
 * already uses for a tinted tile or chip (navy, rose, green, blue, amber, grey).
 */
export const KIND_META: Record<VisitKind, { icon: string; tile: string }> = {
  office: { icon: "lucide:stethoscope", tile: "bg-[#EEF2F7] text-[#0A2947]" },
  prenatal: { icon: "lucide:heart", tile: "bg-[#FFF0F3] text-[#E4002B]" },
  postpartum: { icon: "lucide:baby", tile: "bg-[#FFF2F4] text-[#C8102E]" },
  procedure: { icon: "lucide:activity", tile: "bg-[#ECFDF3] text-[#027A48]" },
  imaging: { icon: "lucide:scan-line", tile: "bg-[#EFF6FF] text-[#1D4ED8]" },
  hospital: { icon: "lucide:bed-double", tile: "bg-[#EEF0F5] text-[#180047]" },
  emergency: { icon: "lucide:ambulance", tile: "bg-[#FEF2F2] text-[#DC2626]" },
  telehealth: { icon: "lucide:video", tile: "bg-[#EFF6FF] text-[#2563EB]" },
  phone_call: { icon: "lucide:phone", tile: "bg-[#ECFDF3] text-[#16A34A]" },
  message: { icon: "lucide:message-square", tile: "bg-[#FFFBEB] text-[#B45309]" },
  letter: { icon: "lucide:mail", tile: "bg-[#FEF6DC] text-[#946200]" },
  results: { icon: "lucide:clipboard-check", tile: "bg-[#EFF6FF] text-[#1D4ED8]" },
  chart_note: { icon: "lucide:file-text", tile: "bg-[#F3F4F6] text-[#374151]" },
  other: { icon: "lucide:file", tile: "bg-[#F3F4F6] text-[#374151]" },
};

const CLASS_KIND: Record<EncounterClass, VisitKind> = {
  office: "office",
  hospital: "hospital",
  procedure: "procedure",
  imaging: "imaging",
  emergency: "emergency",
  telehealth: "telehealth",
  phone_call: "phone_call",
  portal_message: "message",
  results_review: "results",
  referral: "letter",
  addendum: "chart_note",
  letter: "letter",
  other: "other",
};

// function(visitKind)
/** The backend's kind, else worked out from the class (and the type name for prenatal / postpartum). */
export function visitKind(encounter: Pick<Encounter, "kind" | "class" | "typeLabel" | "typeRaw">): VisitKind {
  if (encounter.kind && encounter.kind in KIND_META) return encounter.kind;
  const base = CLASS_KIND[encounter.class] ?? "other";
  if (base === "office") {
    const name = recordTypeLabel(encounter).toLowerCase();
    if (name.includes("prenatal")) return "prenatal";
    if (name.includes("postpartum")) return "postpartum";
  }
  if (base === "other" && recordTypeLabel(encounter) === "Chart note") return "chart_note";
  return base;
}

// function(kindMeta)
export function kindMeta(kind: VisitKind | undefined): { icon: string; tile: string } {
  return KIND_META[kind ?? "other"] ?? KIND_META.other;
}

// const(BANNED_STRINGS)
/**
 * Wording that never renders on a Care & Treatment screen: placeholders, internal labels and
 * repeated chips. Short words ("Shown") are banned as a whole label, not inside
 * a sentence ("Shown exactly as written in your record." is required).
 */
export const BANNED_STRINGS: string[] = [
  "Original label",
  "Linked visit: Confirmed by the source",
  "Confirmed by the source",
  "Shown",
  "From a scanned or uploaded document",
  "Found in 1 source",
  // "Where this came from (N sources)" is allowed, one source too: every visit and
  // note heading ends with it, as on Medications (2026-10-29).
  "Not recorded",
  "Not available",
  "Not tracked",
  "Provider not recorded",
  "0 goals",
  "Clinical note · Clinical note",
  "Final",
  "Signed",
];
